import React, { useEffect, useCallback } from 'react';
import { cn } from '../../cn';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  size?: ModalSize;
  className?: string;
  children: React.ReactNode;
}

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-4xl',
  // Near-fullscreen: wide viewers (raw JSON payloads) that want the
  // dialog to own the screen while leaving a small gutter. Height is
  // left to the caller's `className` (e.g. `h-[85vh] flex flex-col`).
  full: 'max-w-[90vw]',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  size = 'md',
  className,
  children,
}) => {
  // Escape key to close
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-overlay/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={cn('bg-card rounded-lg w-full shadow-xl', sizeClass[size], className)}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

interface ModalSectionProps {
  className?: string;
  children?: React.ReactNode;
}

export const ModalHeader: React.FC<ModalSectionProps & { onClose?: () => void }> = ({
  className,
  onClose,
  children,
}) => (
  <div className={cn('px-6 py-4 border-b border-border', className)}>
    <div className="flex justify-between items-center">
      <div>{children}</div>
      {onClose && (
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  </div>
);

export const ModalTitle: React.FC<ModalSectionProps> = ({ className, children }) => (
  <h2 className={cn('text-lg font-semibold', className)}>{children}</h2>
);

export const ModalDescription: React.FC<ModalSectionProps> = ({ className, children }) => (
  <p className={cn('text-sm text-muted-foreground mt-1', className)}>{children}</p>
);

export const ModalBody: React.FC<ModalSectionProps> = ({ className, children }) => (
  <div className={cn('px-6 py-4', className)}>{children}</div>
);

export const ModalFooter: React.FC<ModalSectionProps> = ({ className, children }) => (
  <div className={cn('px-6 py-4 border-t border-border flex justify-end gap-3', className)}>
    {children}
  </div>
);
